import { useCallback, useMemo } from 'react';
import type { FC, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { ShippingOptionType } from '../../bff-api/baskets/model';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { useAddProductToBag } from '../../hooks/useAddProductToBag';
import { EventAction, EventLocation, useBloomreach } from '../../hooks/useBloomreach';
import { IconButton } from '../button/icon-button';
import { Text } from '../typography';
import type { Product } from '../../bff-api/products/model';

const CurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const suggestedLabel = (
    <Text color="slt-pepper.700" size="small">
        <FormattedMessage id="suggested" defaultMessage="Sugg." />
    </Text>
);

interface ProductPriceProps {
    product: Product;
    hideSuggLabel?: boolean;
    widgetId?: string;
    widgetType?: string;
    metadataType?: string;
    location?: RecommendationLocation;
    onAddProductToBag?: (product: Product) => void;
}

export const ProductPrice: FC<ProductPriceProps> = ({
    product,
    location,
    widgetId,
    widgetType,
    metadataType,
    onAddProductToBag,
    hideSuggLabel = false,
}) => {
    const isDrawer = location === RecommendationLocation.DRAWER;

    const { price = 0, prices = {}, c_canonicalProductId } = product;
    const { suggestedPrice, regularPrice } = prices;
    const { widgetClicked } = useBloomreach();
    const addProductToBag = useAddProductToBag({
        ignoreInventory: true,
        openDrawer: true,
        concatAddedProducts: isDrawer,
    });
    const isPriceHeader = useBreakpointValue({ base: false, sm: true });

    const iconName = 'plus-circle';

    const onAddToBag: MouseEventHandler<HTMLButtonElement> = useCallback(
        async (evt): Promise<void> => {
            evt.preventDefault();
            evt.stopPropagation();
            const variant = {
                ...product,
                id: c_canonicalProductId || product.id,
            };

            if (widgetId && widgetType && metadataType) {
                widgetClicked({
                    location: EventLocation.cart,
                    action: EventAction['widget-add'],
                    data: {
                        widgetResponseId: uuidv4(),
                        widgetId,
                        widgetType,
                        metadataType,
                        itemId: product.id,
                        sku: variant.id,
                    },
                });
            }

            await addProductToBag.mutateAsync({
                product: variant,
                quantity: 1,
                shippingOptionType: ShippingOptionType.SHIP_TO,
                applyExtend: false,
            });

            if (onAddProductToBag) {
                onAddProductToBag(product);
            }
        },
        [
            onAddProductToBag,
            widgetClicked,
            addProductToBag,
            c_canonicalProductId,
            product,
            widgetId,
            widgetType,
            metadataType,
        ],
    );

    const iconButton = useMemo(
        () =>
            onAddProductToBag ? (
                <IconButton
                    id="add-to-bag"
                    iconName={iconName}
                    onClick={onAddToBag}
                    size="lg"
                    isDisabled={addProductToBag.isLoading}
                    isLoading={addProductToBag.isLoading}
                />
            ) : null,
        [onAddToBag, iconName, addProductToBag?.isLoading, onAddProductToBag],
    );

    if (suggestedPrice && price) {
        return (
            <PdpPriceContainer isDrawer={isDrawer} iconButton={iconButton}>
                {isPriceHeader && !isDrawer ? (
                    <Text color="slt-red.1000" margin="0 4px 0 0" style="bolder" size="base">
                        <>
                            <FormattedMessage id="sale" defaultMessage="Sale" />{' '}
                            {CurrencyFormatter.format(price)}
                        </>
                    </Text>
                ) : (
                    <Text margin="0 4px 0 0" color="slt-red.1000" as="h4" style="bold">
                        <>
                            <FormattedMessage id="sale" defaultMessage="Sale" />{' '}
                            {CurrencyFormatter.format(price)}
                        </>
                    </Text>
                )}
                <Flex>
                    {!hideSuggLabel && suggestedLabel}
                    <Text size="small" type="cross" color="slt-pepper.700">
                        {CurrencyFormatter.format(suggestedPrice)}
                    </Text>
                </Flex>
            </PdpPriceContainer>
        );
    }

    if (regularPrice) {
        if (price) {
            return (
                <PdpPriceContainer isDrawer={isDrawer} iconButton={iconButton}>
                    {isPriceHeader && !isDrawer ? (
                        <Text color="slt-red.1000" style="bold" size="base">
                            <>
                                <FormattedMessage id="sale" defaultMessage="Sale" />{' '}
                                {CurrencyFormatter.format(price)}
                            </>
                        </Text>
                    ) : (
                        <Text color="slt-red.1000" as="h4" style="bold" size="base">
                            <>
                                <FormattedMessage id="sale" defaultMessage="Sale" />{' '}
                                {CurrencyFormatter.format(price)}
                            </>
                        </Text>
                    )}
                    <Text size="small" type="cross" color="slt-pepper.700">
                        {CurrencyFormatter.format(regularPrice)}
                    </Text>
                </PdpPriceContainer>
            );
        }

        if (suggestedPrice) {
            return (
                <PdpPriceContainer isDrawer={isDrawer} iconButton={iconButton}>
                    {isPriceHeader && !isDrawer ? (
                        <Text color="slt-pepper.1000" style="bold">
                            {CurrencyFormatter.format(regularPrice)}
                        </Text>
                    ) : (
                        <Text color="slt-pepper.1000" as="h4" style="bold">
                            {CurrencyFormatter.format(regularPrice)}
                        </Text>
                    )}
                    <Flex>
                        {suggestedLabel}
                        <Text size="small" type="cross" color="slt-pepper.700">
                            {CurrencyFormatter.format(suggestedPrice)}
                        </Text>
                    </Flex>
                </PdpPriceContainer>
            );
        }

        return (
            <PdpPriceContainer isDrawer={isDrawer} iconButton={iconButton}>
                {isPriceHeader && !isDrawer ? (
                    <Text color="slt-pepper.1000" style="bold">
                        {CurrencyFormatter.format(regularPrice)}
                    </Text>
                ) : (
                    <Text color="slt-pepper.1000" as="h4" style="bold">
                        {CurrencyFormatter.format(regularPrice)}
                    </Text>
                )}
            </PdpPriceContainer>
        );
    }

    if (price) {
        return (
            <PdpPriceContainer isDrawer={isDrawer} iconButton={iconButton}>
                {isPriceHeader && !isDrawer ? (
                    <Text id={`product-${product.id}-price`} color="slt-pepper.1000" style="bold">
                        {CurrencyFormatter.format(price)}
                    </Text>
                ) : (
                    <Text
                        id={`product-${product.id}-price`}
                        color="slt-pepper.1000"
                        style="bold"
                        {...(!isDrawer ? { as: 'h4' } : null)}
                    >
                        {CurrencyFormatter.format(price)}
                    </Text>
                )}
            </PdpPriceContainer>
        );
    }

    return null;
};

ProductPrice.defaultProps = {
    hideSuggLabel: false,
    widgetId: undefined,
    widgetType: undefined,
    metadataType: undefined,
    location: undefined,
    onAddProductToBag: undefined,
};

type PriceContainerProps = PropsWithChildren<{ iconButton: ReactNode; isDrawer: boolean }>;

const PdpPriceContainer: FC<PriceContainerProps> = ({ children, iconButton, isDrawer }) => {
    if (!isDrawer) {
        return (
            <Box
                display="flex"
                justifyContent="space-between"
                flexDirection={{ base: 'column', sm: 'row' }}
                className="h-full min-h-[3.125rem] w-full space-y-4 sm:space-x-4 sm:space-y-0"
            >
                <Box display="flex" flexDirection="column" className="space-y-1">
                    {children}
                </Box>
                {iconButton && (
                    <Flex justifyContent="flex-end" alignItems="flex-end">
                        {iconButton}
                    </Flex>
                )}
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="space-between" w="100%" className="sm:space-x-4">
            <Box display="flex" flexDirection="column" className="space-y-1">
                {children}
            </Box>
            {iconButton}
        </Box>
    );
};
